import { useMemo, useState } from 'react';
import { addDays, addWeeks, isSameDay, startOfDay, startOfWeek } from 'date-fns';
import { ChevronLeft, ChevronRight, CalendarDays, Calendar } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// TimeGrid — rejilla horaria reutilizable (base de la agenda tipo Outlook, C-1).
//
// Componente PURO de presentación: recibe una lista de eventos con un shape
// genérico { id, title, start, end, color?, meta? } y los pinta como BLOQUES
// posicionados por hora de inicio y con altura proporcional a su duración, en
// vista DÍA o SEMANA (lunes→domingo). Es agnóstico del origen de datos: da igual
// que los eventos sean sesiones presenciales o vídeo-análisis. NO consulta nada.
//
// Decisiones de diseño (ver PR):
//  - Posición: cada columna-día mide (hourEnd-hourStart)*HOUR_HEIGHT_PX px de alto.
//    top = (minutosDesdeHourStart / 60) * HOUR_HEIGHT_PX; height ídem con la
//    duración. Los eventos se recortan al rango visible [hourStart, hourEnd].
//  - Un evento pertenece al día de su `start` (no se parten a medianoche); si su
//    fin cae fuera del rango visible, se recorta a hourEnd.
//  - Solapes: algoritmo de columnas. Los eventos que se solapan en cadena forman
//    un "cluster"; dentro del cluster cada evento va a la primera columna libre;
//    el ancho del cluster se reparte a partes iguales (col/cols).
//  - `now` es inyectable (prop) para resaltar hoy / la hora actual de forma
//    determinista en tests.
// ─────────────────────────────────────────────────────────────────────────────

export interface TimeGridEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  /** Color de acento del bloque (hex). Por defecto dorado de marca. */
  color?: string;
  /** Carga útil libre para el consumidor (p. ej. la sesión / booking original). */
  meta?: unknown;
}

export type TimeGridView = 'day' | 'week';

export interface TimeGridProps {
  events: TimeGridEvent[];
  /**
   * Fecha de referencia CONTROLADA. Si se pasa, el componente no mantiene la
   * fecha en estado interno: anterior/siguiente/hoy emiten vía `onDateChange` y
   * la pantalla decide. Si se omite, cae al modo no controlado (estado interno).
   */
  date?: Date;
  /** Notifica el cambio de fecha (anterior/siguiente/hoy). Necesario si `date`. */
  onDateChange?: (date: Date) => void;
  /** Vista CONTROLADA ('day'|'week'). Si se pasa, prevalece sobre el estado interno. */
  view?: TimeGridView;
  /** Vista inicial en modo NO controlado. Por defecto 'week'. */
  defaultView?: TimeGridView;
  /** Se notifica al alternar día/semana. */
  onViewChange?: (view: TimeGridView) => void;
  /** Rango horario visible. Por defecto 8–24. */
  hourStart?: number;
  hourEnd?: number;
  /** Fecha inicial en modo NO controlado (semilla del estado interno). Por defecto = `now`. */
  initialDate?: Date;
  /** Instante "actual" para resaltar hoy y la hora actual. Por defecto new Date(). */
  now?: Date;
  onEventClick?: (event: TimeGridEvent) => void;
}

/** Alto en px de una hora de rejilla. Exportado para posicionar/testear. */
export const HOUR_HEIGHT_PX = 48;

const DEFAULT_COLOR = '#FFF200';
const MIN_BLOCK_PX = 16;

const WEEKDAYS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTHS_ES = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
];

function minutesOfDay(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

/**
 * Reparte en columnas los eventos de un mismo día para que los solapes se vean
 * todos. Devuelve, por evento, su índice de columna y el nº total de columnas de
 * su cluster. Puro y exportado para test.
 */
export function layoutDayColumns(
  events: TimeGridEvent[]
): Array<{ event: TimeGridEvent; col: number; cols: number }> {
  const sorted = [...events].sort(
    (a, b) => a.start.getTime() - b.start.getTime() || a.end.getTime() - b.end.getTime()
  );

  const out: Array<{ event: TimeGridEvent; col: number; cols: number }> = [];
  let cluster: TimeGridEvent[] = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    if (!cluster.length) return;
    // columns[i] = último evento colocado en la columna i.
    const columns: TimeGridEvent[] = [];
    const placed = new Map<string, number>();
    for (const ev of cluster) {
      let target = -1;
      for (let i = 0; i < columns.length; i++) {
        if (columns[i].end.getTime() <= ev.start.getTime()) {
          target = i;
          break;
        }
      }
      if (target === -1) {
        columns.push(ev);
        target = columns.length - 1;
      } else {
        columns[target] = ev;
      }
      placed.set(ev.id, target);
    }
    const cols = columns.length;
    for (const ev of cluster) out.push({ event: ev, col: placed.get(ev.id) ?? 0, cols });
    cluster = [];
    clusterEnd = -Infinity;
  };

  for (const ev of sorted) {
    if (cluster.length && ev.start.getTime() >= clusterEnd) flush();
    cluster.push(ev);
    clusterEnd = Math.max(clusterEnd, ev.end.getTime());
  }
  flush();

  return out;
}

/**
 * Rectángulo (top/height en px) de un evento dentro del rango visible, o null si
 * queda totalmente fuera. Puro y exportado para test.
 */
export function eventRect(
  event: TimeGridEvent,
  hourStart: number,
  hourEnd: number
): { top: number; height: number } | null {
  const rangeStart = hourStart * 60;
  const rangeEnd = hourEnd * 60;
  const startMin = minutesOfDay(event.start);
  // Un fin a las 00:00 del día siguiente se trata como fin de jornada.
  const rawEnd = minutesOfDay(event.end);
  const endMin = rawEnd <= startMin ? rangeEnd : rawEnd;

  const clampedStart = Math.max(startMin, rangeStart);
  const clampedEnd = Math.min(endMin, rangeEnd);
  if (clampedEnd <= rangeStart || clampedStart >= rangeEnd) return null;

  const top = ((clampedStart - rangeStart) / 60) * HOUR_HEIGHT_PX;
  const height = Math.max(((clampedEnd - clampedStart) / 60) * HOUR_HEIGHT_PX, MIN_BLOCK_PX);
  return { top, height };
}

export default function TimeGrid({
  events,
  date,
  onDateChange,
  view: viewProp,
  defaultView = 'week',
  onViewChange,
  hourStart = 8,
  hourEnd = 24,
  initialDate,
  now,
  onEventClick,
}: TimeGridProps) {
  // Estable entre renders salvo que cambie la prop `now` (evita recalcular los
  // useCallback que dependen del instante actual).
  const nowDate = useMemo(() => now ?? new Date(), [now]);

  // Fecha y vista pueden venir CONTROLADAS por la pantalla. Si no, se mantiene
  // estado interno (modo no controlado, compat con el uso original).
  const dateControlled = date !== undefined;
  const viewControlled = viewProp !== undefined;
  const [internalDate, setInternalDate] = useState<Date>(() => startOfDay(initialDate ?? nowDate));
  const [internalView, setInternalView] = useState<TimeGridView>(defaultView);
  const refDate = startOfDay(dateControlled ? date : internalDate);
  const view = viewControlled ? viewProp : internalView;

  const days = useMemo<Date[]>(() => {
    if (view === 'day') return [startOfDay(refDate)];
    const monday = startOfWeek(refDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  }, [view, refDate]);

  const hours = useMemo<number[]>(
    () => Array.from({ length: hourEnd - hourStart }, (_, i) => hourStart + i),
    [hourStart, hourEnd]
  );

  const bodyHeight = (hourEnd - hourStart) * HOUR_HEIGHT_PX;

  const changeView = (v: TimeGridView) => {
    if (!viewControlled) setInternalView(v);
    onViewChange?.(v);
  };

  // Emite la nueva fecha (día completo). En no controlado también actualiza el
  // estado interno; en controlado solo notifica y la pantalla decide.
  const emitDate = (d: Date) => {
    const nd = startOfDay(d);
    if (!dateControlled) setInternalDate(nd);
    onDateChange?.(nd);
  };

  const goPrev = () => emitDate(view === 'day' ? addDays(refDate, -1) : addWeeks(refDate, -1));
  const goNext = () => emitDate(view === 'day' ? addDays(refDate, 1) : addWeeks(refDate, 1));
  const goToday = () => emitDate(nowDate);

  const rangeLabel = useMemo(() => {
    if (view === 'day') {
      const d = days[0];
      return `${WEEKDAYS_ES[d.getDay()]} ${d.getDate()} ${MONTHS_ES[d.getMonth()]} ${d.getFullYear()}`;
    }
    const a = days[0];
    const b = days[days.length - 1];
    return `${a.getDate()} ${MONTHS_ES[a.getMonth()]} – ${b.getDate()} ${MONTHS_ES[b.getMonth()]} ${b.getFullYear()}`;
  }, [view, days]);

  return (
    <div className="card" data-testid="time-grid" data-view={view}>
      {/* Barra de control: navegación + toggle de vista. */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goPrev}
            data-testid="tg-prev"
            aria-label="Anterior"
            className="p-1.5 rounded-lg border border-bg-border text-text-secondary hover:text-white hover:border-gold-primary/40 transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={goToday}
            data-testid="tg-today"
            className="px-3 py-1.5 rounded-lg border border-bg-border text-sm font-inter text-text-secondary hover:text-white hover:border-gold-primary/40 transition-colors"
          >
            Hoy
          </button>
          <button
            type="button"
            onClick={goNext}
            data-testid="tg-next"
            aria-label="Siguiente"
            className="p-1.5 rounded-lg border border-bg-border text-text-secondary hover:text-white hover:border-gold-primary/40 transition-colors"
          >
            <ChevronRight size={16} />
          </button>
          <span
            data-testid="tg-range-label"
            className="ml-2 font-oswald text-white uppercase tracking-wide text-sm"
          >
            {rangeLabel}
          </span>
        </div>

        <div className="flex gap-1 bg-bg-surface border border-bg-border rounded-xl p-1 w-fit">
          <button
            type="button"
            onClick={() => changeView('day')}
            data-testid="tg-view-day"
            aria-pressed={view === 'day'}
            className={`px-4 py-1.5 rounded-lg text-sm font-inter flex items-center gap-2 transition-colors ${
              view === 'day'
                ? 'bg-gold-primary text-bg-primary font-semibold'
                : 'text-text-secondary hover:text-white'
            }`}
          >
            <Calendar size={14} /> Día
          </button>
          <button
            type="button"
            onClick={() => changeView('week')}
            data-testid="tg-view-week"
            aria-pressed={view === 'week'}
            className={`px-4 py-1.5 rounded-lg text-sm font-inter flex items-center gap-2 transition-colors ${
              view === 'week'
                ? 'bg-gold-primary text-bg-primary font-semibold'
                : 'text-text-secondary hover:text-white'
            }`}
          >
            <CalendarDays size={14} /> Semana
          </button>
        </div>
      </div>

      {/* Cabecera de días. */}
      <div className="flex border-b border-bg-border">
        <div className="w-14 flex-shrink-0" />
        {days.map((day) => {
          const today = isSameDay(day, nowDate);
          return (
            <div
              key={day.toDateString()}
              data-testid={`tg-day-header-${dateKey(day)}`}
              data-today={today ? '1' : '0'}
              className={`flex-1 text-center py-2 ${today ? 'bg-gold-primary/10' : ''}`}
            >
              <p
                className={`font-oswald text-sm uppercase tracking-wide ${
                  today ? 'text-gold-primary' : 'text-text-secondary'
                }`}
              >
                {WEEKDAYS_ES[day.getDay()]} {day.getDate()}
              </p>
            </div>
          );
        })}
      </div>

      {/* Cuerpo con scroll: gutter de horas + columnas-día. */}
      <div className="flex overflow-y-auto" style={{ maxHeight: 640 }}>
        {/* Gutter de horas. */}
        <div className="w-14 flex-shrink-0 relative" style={{ height: bodyHeight }}>
          {hours.map((h, i) => (
            <div
              key={h}
              className="absolute right-2 text-[11px] font-inter text-text-secondary -translate-y-1/2"
              style={{ top: i * HOUR_HEIGHT_PX }}
            >
              {pad2(h)}:00
            </div>
          ))}
        </div>

        {/* Columnas-día. */}
        {days.map((day) => {
          const today = isSameDay(day, nowDate);
          const dayEvents = events.filter((e) => isSameDay(e.start, day));
          const laid = layoutDayColumns(dayEvents);
          const nowMin = minutesOfDay(nowDate);
          const showNowLine = today && nowMin >= hourStart * 60 && nowMin < hourEnd * 60;
          const nowTop = ((nowMin - hourStart * 60) / 60) * HOUR_HEIGHT_PX;

          return (
            <div
              key={day.toDateString()}
              data-testid={`tg-day-col-${dateKey(day)}`}
              className={`flex-1 relative border-l border-bg-border ${today ? 'bg-gold-primary/[0.03]' : ''}`}
              style={{ height: bodyHeight }}
            >
              {/* Líneas de hora. */}
              {hours.map((h, i) => (
                <div
                  key={h}
                  className="absolute left-0 right-0 border-t border-bg-border/60"
                  style={{ top: i * HOUR_HEIGHT_PX }}
                />
              ))}

              {/* Línea de "ahora". */}
              {showNowLine && (
                <div
                  data-testid="tg-now-line"
                  className="absolute left-0 right-0 border-t-2 border-red-500 z-20 pointer-events-none"
                  style={{ top: nowTop }}
                >
                  <span className="absolute -left-1 -top-1 w-2 h-2 rounded-full bg-red-500" />
                </div>
              )}

              {/* Bloques de evento. */}
              {laid.map(({ event, col, cols }) => {
                const rect = eventRect(event, hourStart, hourEnd);
                if (!rect) return null;
                const widthPct = 100 / cols;
                const color = event.color ?? DEFAULT_COLOR;
                return (
                  <button
                    type="button"
                    key={event.id}
                    data-testid={`tg-event-${event.id}`}
                    data-col={col}
                    data-cols={cols}
                    onClick={() => onEventClick?.(event)}
                    style={{
                      position: 'absolute',
                      top: rect.top,
                      height: rect.height,
                      left: `calc(${col * widthPct}% + 2px)`,
                      width: `calc(${widthPct}% - 4px)`,
                      backgroundColor: `${color}22`,
                      borderColor: `${color}88`,
                    }}
                    className="z-10 overflow-hidden rounded-md border-l-2 px-1.5 py-1 text-left hover:brightness-125 transition-[filter]"
                  >
                    <span className="block font-inter text-[11px] leading-tight text-white truncate">
                      {formatHm(event.start)}
                      {' · '}
                      {event.title}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function formatHm(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
